import { createReducer, createSelector } from '@reduxjs/toolkit';
import {
  all,
  call,
  fork,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { HEARING_INCLUDE, HEARINGROLE_OWNER } from '../../constants/api';
import {
  createCommentContentInitialize,
  createHearingInitialize,
  createHearingSuccess,
  getAllHearingsError,
  getAllHearingsInitialize,
  getAllHearingsSuccess,
  updateCommentContentInitialize,
  updateCommentInitialize,
  updateFieldContentInitialize,
  updateHearingInitialize,
  updateHearingSuccess,
  uploadReviewersInitialize,
  deleteHearingInitialize,
  deleteHearingSuccess,
  updateFieldContentAutoSaveInitialize,
  forceDeleteHearingInitialize,
  forceDeleteHearingSuccess,
  getPaginatedHearingsInitialize,
  getPaginatedHearingsError,
  getPaginatedHearingsSuccess,
  getHearingInitialize,
  getHearingError,
  getHearingSuccess,
  uploadCsvInviteesInitialize,
  uploadExcelInviteesInitialize,
  uploadPersonalInviteesInitialize,
  uploadInvitationGroupInviteesInitialize,
  deleteInvitationSourceInitialize,
  deleteInvitationSourceMappingsInitialize,
  updateFieldContentSuccess,
} from '../actions/api';
import { currentUserIdSelector } from '../authenticationSlice';
import { HearingConnector } from '../connectors/hearingConnector';
import { getEntityId, getRelId } from '../helpers/apiHelpers';
import { removeItemFromList, updateDtoList } from '../helpers/reducerHelpers';
import {
  generateCreateApiCall,
  generateGetAllApiCall,
  generateMultipartFormApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import {
  getIncludedData,
  getRequestData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { allUserHearingRolesWithRoleSelector } from './userHearingRoleSlice';

// -------- Overview --------

// The following endpoints exist
//  - GET /hearing
//  - GET /hearing/{id}
//  - POST /hearing
//  - PATCH /hearing/{id}
//  - DELETE /hearing/{id}

// -------- Reducer --------
const hearingReducer = createReducer(
  {
    all: [],
    isFetchingHearings: false,
  },
  (builder) => {
    builder
      .addCase(createHearingSuccess, (state, action) => {
        const hearing = getRequestData(action);
        return updateDtoList(state, 'all', hearing);
      })
      .addCase(updateHearingSuccess, (state, action) => {
        const hearing = getRequestData(action);
        return updateDtoList(state, 'all', hearing);
      })
      .addCase(getAllHearingsInitialize, (state) => {
        state.isFetchingHearings = true;
      })
      .addCase(getHearingInitialize, (state) => {
        state.isFetchingHearings = true;
      })
      .addCase(getAllHearingsError, (state) => {
        state.isFetchingHearings = false;
      })
      .addCase(getHearingError, (state) => {
        state.isFetchingHearings = false;
      })
      .addCase(deleteHearingSuccess, (state, action) => {
        const deletedHearing = getRequestData(action);
        return removeItemFromList(state, 'all', getEntityId(deletedHearing));
      })
      .addCase(forceDeleteHearingSuccess, (state, action) => {
        const forceDeletedHearing = getRequestData(action);
        return removeItemFromList(
          state,
          'all',
          getEntityId(forceDeletedHearing),
        );
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingHearings: false,
        });
        const hearings = getRequestData(action);
        return updateDtoList(newState, 'all', hearings);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingHearings: false,
        });
        const hearing = getRequestData(action);
        return updateDtoList(newState, 'all', hearing);
      })
      .addCase(getPaginatedHearingsInitialize, (state) => {
        state.isFetchingHearings = true;
      })
      .addCase(getPaginatedHearingsError, (state) => {
        state.isFetchingHearings = false;
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const newState = Object.assign({}, state, {
          isFetchingHearings: false,
        });
        const hearings = getRequestData(action);
        return updateDtoList(newState, 'all', hearings);
      })
      .addCase(updateFieldContentSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearing = data.filter(
          (include) => include.type === HEARING_INCLUDE,
        );
        return updateDtoList(state, 'all', hearing);
      });
  },
);

// -------- Sagas --------
export function* hearingSaga() {
  yield all([
    fork(getHearingSaga),
    fork(createHearingSaga),
    fork(updateHearingSaga),
    fork(getAllHearingsSaga),
    fork(updateFieldContentSaga),
    fork(createCommentContentSaga),
    fork(updateCommentContentSaga),
    fork(updateCommentSaga),
    fork(uploadReviewersSaga),
    fork(uploadCsvInviteesSaga),
    fork(uploadExcelInviteesSaga),
    fork(uploadPersonalInviteesSaga),
    fork(uploadInvitationGroupInviteesSaga),
    fork(deleteHearingSaga),
    fork(forceDeleteHearingSaga),
    fork(getPaginatedHearingsSaga),
    fork(deleteInvitationSourceSaga),
    fork(deleteInvitationSourceMappingsSaga),
  ]);
}

function* getHearingSaga() {
  yield takeLeading(
    getHearingInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.getHearing],
        action.payload.hearingId,
        action.payload.includes,
      );
    }),
  );
}

function* getAllHearingsSaga() {
  yield takeLeading(
    getAllHearingsInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector) {
      return yield call([connector, connector.getHearings]);
    }),
  );
}

function* deleteHearingSaga() {
  yield takeEvery(
    deleteHearingInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.deleteHearing],
        action.payload.hearingId,
      );
    }),
  );
}

function* forceDeleteHearingSaga() {
  yield takeEvery(
    forceDeleteHearingInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.forceDeleteHearing],
        action.payload.hearingId,
      );
    }),
  );
}

function* createHearingSaga() {
  yield takeEvery(
    createHearingInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector) {
      return yield call([connector, connector.createHearing]);
    }),
  );
}
function* updateHearingSaga() {
  yield takeEvery(
    updateHearingInitialize.type,
    generateUpdateApiCall(HearingConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateHearing],
        action.payload.changes.id,
        requestData,
      );
    }),
  );
}

function* getPaginatedHearingsSaga() {
  yield takeLatest(
    getPaginatedHearingsInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.getPaginatedHearings],
        action.payload.includes,
        action.payload.pageIndex,
        action.payload.pageSize,
        action.payload.orderBy,
        action.payload.filters,
      );
    }),
  );
}

function* updateFieldContentSaga() {
  yield takeEvery(
    [
      updateFieldContentInitialize.type,
      updateFieldContentAutoSaveInitialize.type,
    ],
    generateMultipartFormApiCall('Fields', 'file')(HearingConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateFieldContent],
        action.payload.id,
        action.payload.hearingStatusId,
        action.payload.notifyAboutChanges,
        requestData.formData,
      );
    }),
  );
}

function* updateCommentContentSaga() {
  yield takeEvery(
    updateCommentContentInitialize.type,
    generateMultipartFormApiCall(null, ['file', 'commenttype'])(
      HearingConnector,
      function* (connector, action, requestData) {
        return yield call(
          [connector, connector.updateCommentContent],
          action.payload.hearingId,
          action.payload.commentId,
          requestData.formData,
        );
      },
    ),
  );
}

function* createCommentContentSaga() {
  yield takeEvery(
    createCommentContentInitialize.type,
    generateMultipartFormApiCall(null, ['file', 'commentstatus'])(
      HearingConnector,
      function* (connector, action, requestData) {
        return yield call(
          [connector, connector.createCommentContent],
          action.payload.hearingId,
          requestData.formData,
        );
      },
    ),
  );
}

function* updateCommentSaga() {
  yield takeEvery(
    updateCommentInitialize.type,
    generateGetAllApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.updateComment],
        action.payload.hearingId,
        action.payload.commentId,
      );
    }),
  );
}

function* uploadCsvInviteesSaga() {
  yield takeEvery(
    uploadCsvInviteesInitialize.type,
    generateMultipartFormApiCall(null, ['formfile'])(
      HearingConnector,
      function* (connector, action, requestData) {
        return yield call(
          [connector, connector.uploadCsvInvitees],
          action.payload.hearingId,
          action.payload.invitationSourceId,
          requestData.formData,
        );
      },
    ),
  );
}

function* uploadExcelInviteesSaga() {
  yield takeEvery(
    uploadExcelInviteesInitialize.type,
    generateMultipartFormApiCall(null, ['formfile'])(
      HearingConnector,
      function* (connector, action, requestData) {
        return yield call(
          [connector, connector.uploadExcelInvitees],
          action.payload.hearingId,
          action.payload.invitationSourceId,
          requestData.formData,
        );
      },
    ),
  );
}

function* uploadPersonalInviteesSaga() {
  yield takeEvery(
    uploadPersonalInviteesInitialize.type,
    generateCreateApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.uploadPersonalInvitees],
        action.payload.hearingId,
        action.payload.invitationSourceId,
        action.payload.dtos,
      );
    }),
  );
}

function* uploadInvitationGroupInviteesSaga() {
  yield takeEvery(
    uploadInvitationGroupInviteesInitialize.type,
    generateCreateApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.uploadInvitationGroupInvitees],
        action.payload.hearingId,
        action.payload.invitationSourceId,
        action.payload.invitationGroupId,
      );
    }),
  );
}

function* uploadReviewersSaga() {
  yield takeEvery(
    uploadReviewersInitialize.type,
    generateCreateApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.uploadReviewers],
        action.payload.hearingId,
        action.payload.models,
      );
    }),
  );
}

function* deleteInvitationSourceSaga() {
  yield takeEvery(
    deleteInvitationSourceInitialize.type,
    generateUpdateApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.deleteInvitationSource],
        action.payload.hearingId,
        action.payload.invitationSourceName,
      );
    }),
  );
}

function* deleteInvitationSourceMappingsSaga() {
  yield takeEvery(
    deleteInvitationSourceMappingsInitialize.type,
    generateUpdateApiCall(HearingConnector, function* (connector, action) {
      return yield call(
        [connector, connector.deleteInvitationSourceMappings],
        action.payload.hearingId,
        action.payload.invitationSourceMappingIds,
        action.payload.deleteFromAllInvitationSources,
      );
    }),
  );
}

// -------- Selectors --------
const selectGetAllHearings = (state) => selectAllFromApiState(state, 'hearing');

const allHearingsSelector = createSelector(
  selectGetAllHearings,
  (hearings) => hearings,
);
const hearingByIdSelector = (hearingId) =>
  createSelector([allHearingsSelector], (hearings) =>
    hearings.find((hearing) => getEntityId(hearing) === hearingId),
  );

const hearingsByIdsSelector = (hearingIds) =>
  createSelector(allHearingsSelector, (hearings) =>
    hearings.filter((hearing) =>
      hearingIds.some((id) => getEntityId(hearing) === id),
    ),
  );

const allHearingsWithCurrentUserInRole = (role) =>
  createSelector(
    [
      selectGetAllHearings,
      allUserHearingRolesWithRoleSelector(role),
      currentUserIdSelector,
    ],
    (hearings, userHearingRoles, currentUserId) =>
      hearings.filter(
        (h) =>
          !!currentUserId &&
          userHearingRoles.some(
            (uhr) =>
              getRelId(uhr, 'hearing') === getEntityId(h) &&
              getRelId(uhr, 'user') === currentUserId.toString(),
          ),
      ),
  );

const currentUserOwnsHearingByIdSelector = (hearingId) =>
  createSelector(
    allHearingsWithCurrentUserInRole(HEARINGROLE_OWNER),
    (hearings) =>
      hearings.some((hearing) => getEntityId(hearing) === hearingId),
  );

const hearingState = (state) => state.api.hearing;

const isFetchingHearingsSelector = createSelector(
  hearingState,
  (state) => state.isFetchingHearings,
);

// -------- Exporting --------
export {
  hearingByIdSelector,
  hearingsByIdsSelector,
  allHearingsSelector,
  currentUserOwnsHearingByIdSelector,
  allHearingsWithCurrentUserInRole,
  isFetchingHearingsSelector,
};
export default hearingReducer;
